import { useState } from "react";
import type { CivilDate } from "../../lib/civilDate";
import { formatCivilDate, formatCivilDateLabel, parseCivilDate } from "../../lib/civilDate";
import { Button } from "./Button";
import { Calendar, CalendarRange } from "./Calendar";
import { CONTROL, FieldShell } from "./Input";
import { Popover, PopoverPanel, PopoverTrigger } from "./Popover";

/**
 * The two date controls the panel offers: one day, and a span of days.
 *
 * ## Why these replaced `<input type="date">`
 *
 * The native control is the right answer for most things and was the right answer
 * here until the custom energy window arrived. Three reasons it stopped being:
 *
 * 1. It has no concept of a range, so a window was four separate fields and the
 *    operator had to hold the relationship between them in their head. Picking
 *    "all of August" meant typing two dates that only mean something together.
 * 2. Its rendering is the browser's, and the browser's is a different shape in
 *    every engine — a text box in one, a spinner triple in another, a full-width
 *    modal on a phone. A panel built on one type scale cannot include a control
 *    that ignores it.
 * 3. Its input format is the *browser's* locale, not the deployment's, while the
 *    value it submits is always ISO. An operator on a US-configured machine
 *    reading `08/09/2026` cannot tell whether they typed August or September, and
 *    the field will accept either without complaint.
 *
 * The time fields beside these are deliberately still native. `<input type="time">`
 * takes a typed `14:30` faster than any picker can be clicked, which is the
 * opposite of the trade-off above — the same reasoning that keeps `SelectField` a
 * native `<select>`.
 *
 * ## Why the trigger is a button and not a text box
 *
 * A text box invites typing, and a typed date has to be parsed, which puts the
 * locale ambiguity straight back. The trigger states the date in the panel's own
 * unambiguous spelling — `1 Aug 2026`, never `08/09` — and the only way to change
 * it is to pick a day.
 *
 * The cost is that a keyboard user cannot type a date any more, and the mitigation
 * is that the calendar is fully keyboard-driven: arrows move by day, PageUp/Down
 * by month, Enter selects. What is genuinely lost is the ability to reach a date
 * years away in one keystroke, which is why `Clear` is in the panel rather than
 * being a matter of selecting the text and deleting it.
 */

const TRIGGER = [
  CONTROL,
  "flex items-center justify-between gap-2 text-left cursor-pointer",
  // The trigger is not disabled-able and has no placeholder pseudo-element, so
  // the two `CONTROL` rules for those are inert here — kept anyway rather than
  // forking the token, because a second underlined-control style is how the two
  // drift apart.
  "focus-visible:border-signal",
].join(" ");

function TriggerChevron() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="11"
      height="11"
      aria-hidden="true"
      focusable="false"
      className="shrink-0 text-ink-faint"
    >
      <path d="M3 6l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

/** Empty and malformed both read as "nothing chosen" on the trigger; the caller says which. */
function civilOrUndefined(raw: string): CivilDate | undefined {
  const parsed = parseCivilDate(raw);
  return parsed === "absent" || parsed === "invalid" ? undefined : parsed;
}

export interface DateFieldProps {
  readonly label: string;
  /** `YYYY-MM-DD`, or empty for unset. The same wire shape the native input used. */
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Which calendar decides what "today" is. */
  readonly zone: string;
  readonly placeholder?: string | undefined;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
}

export function DateField({
  label,
  value,
  onChange,
  zone,
  placeholder = "Any date",
  hint,
  error,
}: DateFieldProps) {
  const [open, setOpen] = useState(false);
  const selected = civilOrUndefined(value);

  return (
    <FieldShell
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
      render={({ id, labelId, describedBy, invalid }) => (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger>
            <button
              type="button"
              id={id}
              aria-labelledby={`${labelId} ${id}`}
              aria-invalid={invalid}
              {...(describedBy === undefined ? {} : { "aria-describedby": describedBy })}
              className={`${TRIGGER} ${invalid ? "border-signal" : ""}`}
            >
              <span className={selected === undefined ? "text-ink-faint" : ""}>
                {selected === undefined ? placeholder : formatCivilDateLabel(selected)}
              </span>
              <TriggerChevron />
            </button>
          </PopoverTrigger>
          <PopoverPanel label={label}>
            <Calendar
              zone={zone}
              selected={selected}
              onSelect={(date) => {
                onChange(date === undefined ? "" : formatCivilDate(date));
                // One click is the whole interaction for a single date, so the
                // panel closes on it rather than waiting to be dismissed.
                setOpen(false);
              }}
            />
            <PanelFooter
              onClear={
                selected === undefined
                  ? undefined
                  : () => {
                      onChange("");
                      setOpen(false);
                    }
              }
            />
          </PopoverPanel>
        </Popover>
      )}
    />
  );
}

export interface DateRangeFieldProps {
  readonly label: string;
  /** Both `YYYY-MM-DD` or empty. Either end may be set without the other. */
  readonly from: string;
  readonly to: string;
  /**
   * Both ends at once, because one click can change both — starting a new range
   * clears the old end — and two separate writes would put a window through a
   * state the operator never asked for.
   */
  readonly onChange: (range: { readonly from: string; readonly to: string }) => void;
  readonly zone: string;
  readonly placeholder?: string | undefined;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
}

export function DateRangeField({
  label,
  from,
  to,
  onChange,
  zone,
  placeholder = "Choose two dates",
  hint,
  error,
}: DateRangeFieldProps) {
  const [open, setOpen] = useState(false);
  /**
   * Whether a start has been clicked and the panel is waiting for the end.
   *
   * Needed because the payload cannot say. `react-day-picker` answers the *first*
   * click of a range with `{ from: X, to: X }` — a one-day range — which is
   * byte-for-byte what a finished one-day range looks like. Closing on "both ends
   * are set" therefore closed the panel on the first click and left the operator
   * with a window one day wide that they had not finished asking for.
   *
   * Reset whenever the panel opens: an operator returning to a complete range is
   * starting a new one, not continuing the old.
   */
  const [awaitingEnd, setAwaitingEnd] = useState(false);
  const fromDate = civilOrUndefined(from);
  const toDate = civilOrUndefined(to);

  return (
    <FieldShell
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
      render={({ id, labelId, describedBy, invalid }) => (
        <Popover
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (next) setAwaitingEnd(false);
          }}
        >
          <PopoverTrigger>
            <button
              type="button"
              id={id}
              aria-labelledby={`${labelId} ${id}`}
              aria-invalid={invalid}
              {...(describedBy === undefined ? {} : { "aria-describedby": describedBy })}
              className={`${TRIGGER} ${invalid ? "border-signal" : ""}`}
            >
              <span
                className={fromDate === undefined && toDate === undefined ? "text-ink-faint" : ""}
              >
                {rangeLabel(fromDate, toDate, placeholder)}
              </span>
              <TriggerChevron />
            </button>
          </PopoverTrigger>
          <PopoverPanel label={label}>
            <CalendarRange
              zone={zone}
              from={fromDate}
              to={toDate}
              onSelect={(range) => {
                const start = range.from === undefined ? "" : formatCivilDate(range.from);
                const end = range.to === undefined ? "" : formatCivilDate(range.to);

                if (!awaitingEnd) {
                  /**
                   * The opening click. Only the start is written, and the end is
                   * cleared even though the calendar reported one, because the
                   * operator has not chosen it yet — leaving the mirrored value in
                   * the URL would resolve a real one-day window, put an energy
                   * column on the table and fire a query for a range nobody asked
                   * for. Cleared, it resolves to `pending`, which is what the
                   * "Choose an end date." hint is for.
                   */
                  setAwaitingEnd(true);
                  onChange({ from: start, to: "" });
                  return;
                }

                setAwaitingEnd(false);
                onChange({ from: start, to: end });
                // Held open until here. Dismissing after the first click would
                // leave a half-specified window with no obvious way back to the
                // second one.
                setOpen(false);
              }}
            />
            <PanelFooter
              onClear={
                fromDate === undefined && toDate === undefined
                  ? undefined
                  : () => {
                      setAwaitingEnd(false);
                      onChange({ from: "", to: "" });
                      setOpen(false);
                    }
              }
            />
          </PopoverPanel>
        </Popover>
      )}
    />
  );
}

/**
 * What the trigger says. A half-chosen range names the end it has rather than
 * falling back to the placeholder — the operator has told the panel something and
 * the control should show it.
 */
function rangeLabel(
  from: CivilDate | undefined,
  to: CivilDate | undefined,
  placeholder: string,
): string {
  if (from === undefined && to === undefined) return placeholder;
  if (from !== undefined && to === undefined) return `${formatCivilDateLabel(from)} — …`;
  if (from === undefined && to !== undefined) return `… — ${formatCivilDateLabel(to)}`;

  const [start, end] = [from as CivilDate, to as CivilDate];
  const sameDay = start.year === end.year && start.month === end.month && start.day === end.day;

  return sameDay
    ? formatCivilDateLabel(start)
    : `${formatCivilDateLabel(start)} — ${formatCivilDateLabel(end)}`;
}

/**
 * The panel's one action. Absent when there is nothing to clear, rather than
 * disabled: a dead control the operator has to reason about is worse than the
 * row not being there.
 */
function PanelFooter({ onClear }: { readonly onClear?: (() => void) | undefined }) {
  if (onClear === undefined) return null;

  return (
    <div className="mt-3 flex justify-end border-t border-rule pt-2">
      <Button variant="ghost" onClick={onClear}>
        Clear
      </Button>
    </div>
  );
}
